import React from 'react';
import { Shield, MapPin, Syringe, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { rescueImages } from '../mock';

const PawsitiveProtectors = () => {
  const zoneStats = [
    { zone: 'Sector 14, Gurgaon', collars: 245, animals: 312, vaccinations: 298 },
    { zone: 'DLF Phase 2', collars: 189, animals: 223, vaccinations: 215 },
    { zone: 'Cyber City Area', collars: 167, animals: 198, vaccinations: 192 },
    { zone: 'Old Gurgaon', collars: 134, animals: 176, vaccinations: 168 },
    { zone: 'Sohna Road', collars: 98, animals: 128, vaccinations: 121 }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-bright-mint/90 to-green-500/80 z-10"></div>
        <img
          src={rescueImages[4]}
          alt="Pawsitive Protectors"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <div className="flex items-center justify-center mb-6">
            <Shield className="w-16 h-16" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-6">
            Pawsitive Protectors
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-8">
            Making streets safer for animals through reflective collars, vaccinations, and community care.
          </p>
          <div className="inline-block bg-white text-forest-green px-6 py-3 rounded-full font-bold text-lg">
            5,632 Animals Protected & Counting
          </div>
        </div>
      </section>

      {/* Campaign Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-6">
              What is Pawsitive Protectors?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Pawsitive Protectors is our flagship community program launched in 2021 to protect street animals from nighttime accidents and ensure their health through preventive care.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We distribute reflective collars to make animals visible in the dark, conduct mass vaccination drives, provide deworming treatments, and engage communities in responsible animal care practices.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-none shadow-xl bg-sand-beige">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-warm-amber rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-forest-green mb-3">Reflective Collars</h3>
                <p className="text-gray-700 mb-2">High-visibility collars prevent nighttime accidents</p>
                <div className="text-3xl font-bold text-warm-amber">833</div>
                <p className="text-sm text-gray-600">Distributed this year</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl bg-sand-beige">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-4">
                  <Syringe className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-forest-green mb-3">Vaccinations</h3>
                <p className="text-gray-700 mb-2">Rabies and essential vaccines for community safety</p>
                <div className="text-3xl font-bold text-forest-green">8,112</div>
                <p className="text-sm text-gray-600">Animals vaccinated</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl bg-sand-beige">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-bright-mint rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-forest-green" fill="currentColor" />
                </div>
                <h3 className="text-2xl font-bold text-forest-green mb-3">Deworming</h3>
                <p className="text-gray-700 mb-2">Regular deworming treatments for better health</p>
                <div className="text-3xl font-bold text-bright-mint">4,892</div>
                <p className="text-sm text-gray-600">Treatments provided</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Zone-wise Coverage */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Zone-wise Impact
            </h2>
            <p className="text-xl text-gray-700">Comprehensive coverage across Gurgaon</p>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {zoneStats.map((zone, index) => (
              <Card key={index} className="border-none shadow-lg bg-white">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center space-x-3 mb-4 md:mb-0">
                      <MapPin className="w-6 h-6 text-warm-amber" />
                      <h3 className="text-xl font-bold text-forest-green">{zone.zone}</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-warm-amber">{zone.collars}</div>
                        <p className="text-sm text-gray-600">Collars</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-forest-green">{zone.vaccinations}</div>
                        <p className="text-sm text-gray-600">Vaccinations</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-bright-mint">{zone.animals}</div>
                        <p className="text-sm text-gray-600">Animals</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              How The Program Works
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-warm-amber text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-forest-green mb-2">Community Identification</h3>
                  <p className="text-gray-700">We identify high-risk zones and communities with large street animal populations through field surveys and community reports.</p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-warm-amber text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-forest-green mb-2">Health Camp Setup</h3>
                  <p className="text-gray-700">Mobile veterinary camps are set up in target zones with trained staff, medical supplies, and equipment.</p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-warm-amber text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-forest-green mb-2">Treatment & Protection</h3>
                  <p className="text-gray-700">Animals receive vaccinations, deworming treatments, and reflective collars. Each animal is documented and tracked.</p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-warm-amber text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">4</div>
                <div>
                  <h3 className="text-xl font-bold text-forest-green mb-2">Community Education</h3>
                  <p className="text-gray-700">We conduct awareness sessions teaching communities about animal welfare, responsible feeding, and reporting injuries.</p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-warm-amber text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">5</div>
                <div>
                  <h3 className="text-xl font-bold text-forest-green mb-2">Follow-up Care</h3>
                  <p className="text-gray-700">Regular follow-ups ensure animals remain healthy, collars are intact, and communities are engaged in ongoing care.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Pawsitive Protectors in Action
            </h2>
            <p className="text-xl text-gray-700">See the difference we're making</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {rescueImages.map((image, index) => (
              <div key={index} className="relative h-64 rounded-lg overflow-hidden shadow-lg group">
                <img
                  src={image}
                  alt={`Campaign photo ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-green/70 to-transparent"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold font-heading text-forest-green mb-6">
            Become a Pawsitive Protector
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            Support this life-saving campaign through donations or volunteer to distribute collars in your community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-warm-amber hover:bg-amber-600 text-white text-lg px-8 py-6">
                Support the Campaign
              </Button>
            </Link>
            <Link to="/volunteer">
              <Button size="lg" variant="outline" className="border-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-white text-lg px-8 py-6">
                Volunteer With Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PawsitiveProtectors;